import React from 'react';
import ModalPassword from 'containers/ForgottenPassword/components/ModalPassword';
import AlertReminder from 'containers/Player/components/Alert/AlertReminder';
import PinForm from 'containers/PinForm/PinForm';
import HboActivationModal from 'components/promotions/HboActivationModal';
import HboPromotionModal from 'components/promotions/HboPromotionModal';
import HboErrorModal from 'components/promotions/HboErrorModal';
import { useHboActivationController } from 'containers/HboActivation/hooks/useHboActivationController';
import { HboActivationContext } from 'containers/HboActivation/context/HboActivationContext';
import { selectIsLiveChannel } from 'containers/Player/selectors/media.selector';
import { resolveHboMetricSection } from 'containers/HboActivation/utils/metrics';
import { UNUSED_PARAMETER } from 'hooks/metrics/shared/constants';
import useNodesLayoutData from './hooks/useNodesLayoutData';
import './styles.scss';
import EpgTransactions from '../../containers/Player/components/EpgTransactions/EpgTransactions';
import { Outlet } from 'react-router-dom';
import { useGetContentStyles } from './hooks/useGetContentStyles';
import { useSelector } from 'react-redux';
import { selectHeadbandState } from 'selectors/headband';

const getProviderCodeFromChannel = channel =>
  channel?.group?.common?.extendedcommon?.media?.proveedor?.codigo ||
  channel?.group?.common?.proveedor_code ||
  channel?.group?.universal_id?.content_providers?.[0]?.provider_code ||
  channel?.group?.common?.universal_id?.content_providers?.[0]?.provider_code ||
  channel?.common?.proveedor_code ||
  channel?.universal_id?.content_providers?.[0]?.provider_code ||
  channel?.proveedor_code ||
  '';

const getProviderCodeFromVcardGroup = group =>
  group?.common?.extendedcommon?.media?.proveedor?.codigo ||
  group?.common?.proveedor_code ||
  group?.universal_id?.content_providers?.[0]?.provider_code ||
  group?.common?.universal_id?.content_providers?.[0]?.provider_code ||
  '';

const getStoredEpgLineUpChannel = groupId => {
  if (!groupId) {
    return null;
  }

  try {
    const storedEpgLineUp = JSON.parse(localStorage.getItem('epgLineUp'));
    return (
      storedEpgLineUp?.channels?.find(channel => channel?.group_id === groupId) ||
      null
    );
  } catch (error) {
    return null;
  }
};

const isVcardPath = pathname => (pathname || '').includes('/vcard');

const isPlaybackPath = pathname =>
  ['/watch', '/live', '/event'].some(pathFragment =>
    (pathname || '').includes(pathFragment),
  );

const NodesLayout = ({ children, location, params }) => {
  const { isVisible } = useSelector(selectHeadbandState);
  const selectedNodeCode = useSelector(
    state => state?.cvNavData?.navData?.selected?.code || '',
  );
  const groupIdFromPath = location?.pathname?.split('/')?.[3] || '';
  const isLiveContent = useSelector(selectIsLiveChannel(groupIdFromPath));
  const selectedNodeText = useSelector(
    state => state?.cvNavData?.navData?.selected?.text || '',
  );
  const currentProviderCode = useSelector(state => {
    const pathname = location?.pathname || '';
    const liveChannel = state?.EpgLineUp?.data?.find(
      channel => channel?.group_id === groupIdFromPath,
    );
    const livePlayerChannel = state?.Player?.EpgChannels?.data?.find(
      channel => channel?.group_id === groupIdFromPath,
    );
    const storedLiveChannel = getStoredEpgLineUpChannel(groupIdFromPath);
    const vcardGroup = isVcardPath(pathname) ? state?.Vcard?.content?.group : null;
    const playerContent = isPlaybackPath(pathname) ? state?.Player?.Content?.data : null;

    return (
      getProviderCodeFromChannel(playerContent) ||
      getProviderCodeFromVcardGroup(vcardGroup) ||
      getProviderCodeFromChannel(liveChannel) ||
      getProviderCodeFromChannel(livePlayerChannel) ||
      getProviderCodeFromChannel(storedLiveChannel)
    );
  });
  const hboActivationController = useHboActivationController(location?.pathname || '');
  const {
    showInitialModal: showHboActivationModal,
    showPromotionModal: showHboPromotionModal,
    showErrorModal: showHboErrorModal,
    errorCode: hboErrorCode,
    forceShowPromotion: forceShowHboPromotion,
    comesFromRequestActivation,
    resetPromotionDismissed,
    onDefer: onHboDefer,
    onCloseInitialModal,
    onCancelInitialModal,
    onClosePromotion: onCloseHboPromotion,
    onDismissPermanently: onDismissHboPromotionPermanently,
    onPrimaryAction: onHboActivate,
    onCloseError: onCloseHboError,
  } = hboActivationController;
  const hboContentSection = resolveHboMetricSection({
    pathname: location?.pathname,
    selectedNodeText,
    selectedNodeCode,
    stateSection: location?.state?.hboMetricContext?.contentSection,
  });
  const hboContentSubsection =
    location?.state?.hboMetricContext?.contentSubsection || UNUSED_PARAMETER;
  const {
    renderPictureInPicture,
    renderHeadBand,
    renderLoadingSpinner,
    renderModal,
    renderUserSuspendedModal,
    renderProfilesContainer,
    renderFooter,
    renderOldFooter,
    renderHeader,
    isInSearcher,
    renderLoader,
    showHeadBand,
    noShowHF,
    isMundial2026,
  } = useNodesLayoutData({ location, params });
  const { contentDivRef, footerRef } = useGetContentStyles();

  const renderComponents = () => (
    <>
      {renderPictureInPicture()}
      {renderLoadingSpinner()}
      {renderHeadBand()}
      {<ModalPassword />}
    </>
  );

  return (
    <HboActivationContext.Provider value={hboActivationController}>
      {renderComponents()}
      <div
        id="main-wrap"
        className={`wrap ${showHeadBand && isVisible ? 'with-headband' : ''}`}
      >
        {renderLoader}
        {renderModal()}
        <HboActivationModal
          pathname={location?.pathname}
          selectedNodeCode={selectedNodeCode}
          currentProviderCode={currentProviderCode}
          isLiveContent={isLiveContent === true}
          isEligible={showHboActivationModal}
          hasRecognizedEntryPoint={Boolean(
            Boolean(location?.state?.hboEntryPoint) || comesFromRequestActivation,
          )}
          suppressOnHboNodePage={Boolean(location?.state?.suppressHboInitialModal)}
          // Salir del player VOD devuelve a la VCARD: es una salida, no una entrada al
          // contenido. Una nueva interaccion explicita (requestActivation) lo reactiva.
          isSuppressed={Boolean(
            location?.state?.suppressHboActivationModal &&
              !comesFromRequestActivation,
          )}
          isAutoChannelSelection={Boolean(location?.state?.isAutoChannelSelection)}
          contentSection={hboContentSection}
          contentSubsection={hboContentSubsection}
          onPrimaryAction={onHboActivate}
          onDefer={onHboDefer}
          onCloseComplete={onCloseInitialModal}
          onCancelClose={onCancelInitialModal}
          showErrorModal={showHboErrorModal}
        />
        <HboPromotionModal
          show={showHboPromotionModal}
          pathname={location?.pathname}
          selectedNodeCode={selectedNodeCode}
          currentProviderCode={currentProviderCode}
          hasRecognizedEntryPoint={Boolean(
            Boolean(location?.state?.hboEntryPoint) || comesFromRequestActivation,
          )}
          contentSection={hboContentSection}
          contentSubsection={hboContentSubsection}
          forceShow={forceShowHboPromotion}
          onClose={onCloseHboPromotion}
          onSecondaryAction={onDismissHboPromotionPermanently}
          resetPromotionDismissed={resetPromotionDismissed}
        />
        <HboErrorModal
          show={showHboErrorModal}
          onClose={onCloseHboError}
          contentSection={hboContentSection}
          contentSubsection={hboContentSubsection}
          errorCode={hboErrorCode}
        />
        <AlertReminder />
        {renderProfilesContainer()}
        {renderHeader()}
        <div
          ref={contentDivRef}
          className={`content_div ${isInSearcher ? 'in-search' : ''} ${
            showHeadBand && isVisible ? 'with-headband' : ''
          } ${noShowHF ? 'content_div--no-header' : ''} ${
            isMundial2026 ? 'content_div--mundial2026' : ''
          }`}
        >
          <Outlet />
          {renderOldFooter(footerRef)}
        </div>
        <PinForm />
        <EpgTransactions isFromNodeMenu={true} />
        {renderUserSuspendedModal()}
        {renderFooter()}
      </div>
      <div className="WrapStyle" />
    </HboActivationContext.Provider>
  );
};

export default NodesLayout;
